import React, { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import Seo from "../../components/seo/Seo";
import NavV2 from "../components/NavV2";
import FooterV2 from "../components/FooterV2";
import { Display } from "../components/Primitives";
import {
  useCursor,
  useInkBody,
  useReveal,
  useV2Fonts,
} from "../hooks/useV2Effects";
import "../v2.css";

interface SeoProps {
  title: string;
  description: string;
  canonicalPath: string;
  ogImage?: string;
  jsonLd?: object[];
  noindex?: boolean;
}

/**
 * Shared chrome for every v2 page: fonts, ink body, scroll reveal, cursor
 * ring, grain, nav and footer. Also scrolls to `location.hash` after mount so
 * the nav's section links work when arriving from a sub-page.
 */
const PageShell: React.FC<{ seo: SeoProps; children: React.ReactNode }> = ({
  seo,
  children,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const { hash, pathname } = useLocation();

  useV2Fonts();
  useInkBody();
  useReveal(rootRef);
  useCursor(ringRef);

  useEffect(() => {
    if (!hash) return;
    const id = window.setTimeout(() => {
      document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
    }, 120);
    return () => window.clearTimeout(id);
  }, [hash, pathname]);

  return (
    <div ref={rootRef} className="ltb-v2">
      <Seo {...seo} />
      <NavV2 />
      <main>{children}</main>
      <FooterV2 />
      <div className="v2-grain" style={{ backgroundImage: "url(/noise.png)" }} aria-hidden />
      <div ref={ringRef} className="v2-cursor" aria-hidden />
    </div>
  );
};

export default PageShell;

export interface Crumb {
  label: string;
  to?: string;
}

/** Mono breadcrumb trail, ink context. */
export const Crumbs: React.FC<{ items: Crumb[] }> = ({ items }) => (
  <nav
    aria-label="Breadcrumb"
    className="flex flex-wrap items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-bone/50"
  >
    {items.map((c, i) => (
      <React.Fragment key={i}>
        {i > 0 && <span className="text-bone/25">/</span>}
        {c.to ? (
          <Link to={c.to} className="v2-link hover:text-bone">
            {c.label}
          </Link>
        ) : (
          <span className="text-bone/80" aria-current="page">
            {c.label}
          </span>
        )}
      </React.Fragment>
    ))}
  </nav>
);

interface PageHeroProps {
  crumbs: Crumb[];
  index: string;
  eyebrow: string;
  title: React.ReactNode;
  lede?: React.ReactNode;
  actions?: React.ReactNode;
  /** Right column (illustration). Hidden below lg. */
  aside?: React.ReactNode;
}

/** Editorial page opener on ink: crumbs, mono eyebrow, giant serif title. */
export const PageHero: React.FC<PageHeroProps> = ({
  crumbs,
  index,
  eyebrow,
  title,
  lede,
  actions,
  aside,
}) => (
  <section className="relative isolate overflow-hidden bg-ink pb-14 pt-28 text-bone sm:pb-20 sm:pt-36">
    <div className="v2-glow pointer-events-none absolute -top-40 right-[-15%] -z-10 h-[60vh] w-[60vh] rounded-full bg-tangerine/[0.10] blur-[140px]" />
    <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
      <Crumbs items={crumbs} />
      <div className="mt-10 grid items-end gap-10 lg:grid-cols-12">
        <div className={aside ? "lg:col-span-7" : "lg:col-span-9"}>
          <div className="v2-reveal flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.22em] text-bone/70">
            <span className="opacity-60">{index}</span>
            <span className="h-px w-8 bg-teal" />
            <span>{eyebrow}</span>
          </div>
          <Display as="h1" className="mt-5 text-[clamp(2.75rem,7.2vw,6.75rem)]">
            {title}
          </Display>
          {lede && (
            <p
              className="v2-reveal mt-7 max-w-xl text-base leading-relaxed text-bone/70 sm:text-lg"
              data-delay="2"
            >
              {lede}
            </p>
          )}
          {actions && (
            <div className="v2-reveal mt-8 flex flex-wrap gap-3" data-delay="3">
              {actions}
            </div>
          )}
        </div>
        {aside && (
          <div
            className="v2-reveal hidden lg:col-span-5 lg:block"
            data-delay="2"
          >
            {aside}
          </div>
        )}
      </div>
    </div>
  </section>
);

/** Numbered editorial row list (bone context). */
export const Steps: React.FC<{
  steps: { title: string; desc: string; meta?: string }[];
}> = ({ steps }) => (
  <div className="v2-reveal" data-delay="1">
    {steps.map((s, i) => (
      <div
        key={i}
        className="grid gap-3 border-t border-ink/15 py-7 last:border-b sm:grid-cols-12 sm:gap-8 sm:py-9"
      >
        <div className="font-mono text-[11px] tracking-[0.22em] text-teal-deep sm:col-span-1">
          {`0${i + 1}`}
        </div>
        <h2 className="font-display text-2xl leading-tight sm:col-span-4 sm:text-4xl">
          {s.title}
        </h2>
        <div className="sm:col-span-7">
          <p className="max-w-xl text-ink/65">{s.desc}</p>
          {s.meta && (
            <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/40">
              {s.meta}
            </div>
          )}
        </div>
      </div>
    ))}
  </div>
);

/** Ink CTA plate with a framed illustration. */
export const Plate: React.FC<{
  title: string;
  sub: string;
  cta: React.ReactNode;
  img: string;
  tone?: "bone" | "teal" | "tangerine";
}> = ({ title, sub, cta, img, tone = "bone" }) => {
  const tones = {
    bone: "bg-bone text-ink",
    teal: "bg-teal-deep text-bone",
    tangerine: "bg-tangerine text-ink",
  };
  return (
    <div
      className={`v2-reveal grid overflow-hidden rounded-2xl md:grid-cols-2 ${tones[tone]}`}
    >
      <div className="flex flex-col p-7 sm:p-12">
        <h2 className="font-display text-[clamp(2rem,4.5vw,4rem)] leading-[0.98] tracking-[-0.02em]">
          {title}
        </h2>
        <p className="mt-4 max-w-md opacity-70">{sub}</p>
        <div className="mt-auto pt-8">{cta}</div>
      </div>
      <div className="relative flex min-h-[260px] items-center justify-center p-6 sm:p-10">
        <img
          src={img}
          alt=""
          loading="lazy"
          className="max-h-[360px] w-auto max-w-full object-contain"
        />
      </div>
    </div>
  );
};
